import React, { useState } from "react";
import {
  Typography, Button, Box, Tooltip, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle,
} from "@mui/material";
import { Link } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import config from "../../config";
import { useStripe } from "@stripe/react-stripe-js";
import * as Actions from "../../redux/actions";
import { connect } from "react-redux";

const PricingDialog = ({ plan, handleClose, descriptionElementRef, open, user, jwtToken }) => {
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubscribe = async (e, planId) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${config.SERVER_URL}/api/v1/checkout`,
        { plan_id: planId },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` } }
      );
      if (res.status === 200) {
        await stripe.redirectToCheckout({ sessionId: res.data.sessionId });
      }
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      PaperProps={{
        sx: {
          backgroundColor: "#1a1a1a",
          border: "1px solid #2a2a2a",
          borderRadius: "20px",
          width: { xs: "90vw", sm: "480px" },
          maxWidth: "480px",
        },
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: "22px", color: "#fff" }}>{plan.name}</Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 0.5 }}>
            <Typography sx={{ fontSize: "13px", color: "#888", alignSelf: "flex-start", mt: 0.3 }}>$</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: "32px", color: "#FFC001", lineHeight: 1 }}>
              {(plan.price_cents / 100) % 1 === 0 ? plan.price_cents / 100 : (plan.price_cents / 100).toFixed(2)}
            </Typography>
            <Typography sx={{ color: "#555", fontSize: "13px", alignSelf: "flex-end", mb: 0.4 }}>
              / {plan.name === "Diamond Special" ? "5 years" : plan.interval}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#555", mt: 0.5, "&:hover": { color: "#fff" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers ref={descriptionElementRef} sx={{ borderColor: "#222", px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {plan.offers && plan.offers.map((offer) => (
            <Box key={offer.name} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {offer.value
                ? <CheckCircleIcon sx={{ color: "#FFC001", fontSize: 17, flexShrink: 0 }} />
                : <CancelIcon sx={{ color: "#333", fontSize: 17, flexShrink: 0 }} />
              }
              <Typography sx={{ color: offer.value ? "#ccc" : "#555", fontSize: "14px", flex: 1 }}>
                {offer.name}
              </Typography>
              {(offer.name === "QR Code" || offer.name === "Merch") && (
                <Chip
                  label="Coming Soon"
                  size="small"
                  sx={{ backgroundColor: "rgba(255,192,1,0.12)", color: "#FFC001", border: "1px solid rgba(255,192,1,0.25)", fontSize: "10px", fontWeight: 700, height: 20 }}
                />
              )}
              {offer.description && (
                <Tooltip title={offer.description} arrow enterTouchDelay={0}>
                  <InfoOutlinedIcon sx={{ color: "#444", fontSize: 15, cursor: "pointer", flexShrink: 0, "&:hover": { color: "#FFC001" } }} />
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>

      {error && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography sx={{ color: "#f44336", fontSize: "13px" }}>{error}</Typography>
        </Box>
      )}
      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
        <Button
          onClick={handleClose}
          sx={{ color: "#666", fontWeight: 700, textTransform: "none", borderRadius: "10px", "&:hover": { color: "#ccc", backgroundColor: "rgba(255,255,255,0.05)" } }}
        >
          Cancel
        </Button>

        {!user && (
          <Button
            component={Link}
            to="/auth/login"
            variant="contained"
            sx={{ backgroundColor: "#FFC001", color: "#000", fontWeight: 800, textTransform: "none", borderRadius: "10px", px: 3, "&:hover": { backgroundColor: "#e6ac00" } }}
          >
            Login / Sign Up
          </Button>
        )}

        {user && !user.is_subscribed && (
          <Button
            onClick={(e) => handleSubscribe(e, plan.id)}
            disabled={loading}
            variant="contained"
            sx={{ backgroundColor: "#FFC001", color: "#000", fontWeight: 800, textTransform: "none", borderRadius: "10px", px: 3, "&:hover": { backgroundColor: "#e6ac00" }, "&.Mui-disabled": { backgroundColor: "#333", color: "#666" } }}
          >
            {loading ? "Redirecting…" : "Subscribe Now"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = ({ Auth }) => ({
  user: Auth.user,
  jwtToken: Auth.jwtToken,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(PricingDialog);
